import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AreaChart, Area,
  BarChart, Bar,
  ComposedChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  FaChartLine, FaCalendarCheck, FaRupeeSign, FaCheckCircle,
  FaClock, FaFileAlt, FaChevronDown, FaArrowUp, FaArrowDown,
  FaMinus, FaCoins, FaUsers, FaExclamationTriangle,
  FaRedo, FaTasks, FaFolderOpen, FaGripVertical, FaUndo,
} from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiHelper } from '../../utils/api';
import './Analytics.scss';

const DEFAULT_CHART_ORDER = [
  'revenue-trend',
  'events-revenue',
  'payment-health',
  'revenue-split',
  'booked-services',
  'event-pipeline',
  'invoice-status',
  'quotations-events',
  'profit-trend',
  'team-cost',
  'revenue-per-service',
  'receivables-aging',
  'oldest-unpaid',
  'top-clients',
  'projects-productivity',
];

const ANALYTICS_ORDER_KEY = 'awsmivent_analytics_order_v1';

const sanitizeOrder = (raw) => {
  if (!Array.isArray(raw)) return [...DEFAULT_CHART_ORDER];
  const seen = new Set();
  const clean = [];
  for (const id of raw) {
    if (typeof id !== 'string') continue;
    if (!DEFAULT_CHART_ORDER.includes(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    clean.push(id);
  }
  for (const id of DEFAULT_CHART_ORDER) {
    if (!seen.has(id)) clean.push(id);
  }
  return clean;
};

const RANGE_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
];

const P = {
  indigo: '#6366f1',
  green:  '#10b981',
  orange: '#f97316',
  violet: '#8b5cf6',
  pink:   '#ec4899',
  amber:  '#f59e0b',
  teal:   '#14b8a6',
};
const CHART_COLORS = [P.indigo, P.green, P.orange, P.pink, P.amber, P.violet, P.teal];

const inrFmt = new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
});
const compactInr = (v) => {
  const n = Number(v);
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(1)}Cr`;
  if (n >= 1_00_000)    return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)       return `₹${(n / 1_000).toFixed(1)}K`;
  return `₹${n}`;
};

// ── Chart tooltip ──────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label, formatters = {} }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="analytics-tooltip">
      <p className="analytics-tooltip__label">{label}</p>
      {payload.map((e) => {
        const fmt = formatters[e.dataKey] || 'num';
        const display = fmt === 'inr' ? inrFmt.format(e.value) : e.value;
        return (
          <p key={e.dataKey} className="analytics-tooltip__item" style={{ color: e.color }}>
            <span>{e.name}:</span>
            <strong>{display}</strong>
          </p>
        );
      })}
    </div>
  );
};

// ── Donut % label ──────────────────────────────────────────────────────────────
const PiePctLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.06) return null;
  const R = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  return (
    <text
      x={cx + r * Math.cos(-midAngle * R)}
      y={cy + r * Math.sin(-midAngle * R)}
      fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={11} fontWeight={700}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Small-screen helper: match the SCSS breakpoint (750px) where the
// sortable grid collapses to a single column. On mobile we lock dragging to
// the vertical axis only so cards move top/bottom and never shift the page
// left/right (which caused horizontal page gaps/overflow).
const MOBILE_LAYOUT_QUERY = '(max-width: 750px)';

const useIsMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_LAYOUT_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

// Inline dnd-kit modifier (avoids adding @dnd-kit/modifiers dep):
// zeroes horizontal movement so the drag transform is top/bottom only.
const restrictToVerticalAxis = ({ transform }) => ({
  ...transform,
  x: 0,
});

// ── Chart card wrapper ─────────────────────────────────────────────────────────
// dragHandle: { attributes, listeners } from dnd-kit — attached to grip only,
// so chart tooltips / hover still work everywhere else.
const ChartCard = ({ title, subtitle, children, dragHandle }) => (
  <div className="analytics__card">
    <div className="analytics__card-header">
      <div className="analytics__card-titles">
        <p className="analytics__card-title">{title}</p>
        {subtitle && <p className="analytics__card-subtitle">{subtitle}</p>}
      </div>
      {dragHandle && (
        <button
          type="button"
          className="analytics__drag-handle"
          title="Drag to rearrange"
          aria-label={`Drag ${title} to rearrange`}
          {...dragHandle.attributes}
          {...dragHandle.listeners}
        >
          <FaGripVertical />
        </button>
      )}
    </div>
    {children}
  </div>
);

// ── Sortable wrapper for a chart card (grid strategy) ────────────────────────
// `verticalOnly` is true on small screens: we force x to 0 as a second
// safety net (in addition to the DndContext modifier) so a horizontal finger
// movement can never translate the card sideways and push the page.
const SortableChartItem = ({ id, spanFull, verticalOnly, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const safeTransform = verticalOnly && transform ? { ...transform, x: 0 } : transform;
  const style = {
    transform: CSS.Transform.toString(safeTransform),
    transition,
    zIndex: isDragging ? 5 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`analytics__sortable-item${spanFull ? ' analytics__sortable-item--full' : ''}${isDragging ? ' analytics__sortable-item--dragging' : ''}`}
    >
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </div>
  );
};

const Empty = ({ msg = 'No data for this period.' }) => (
  <div className="analytics__empty">{msg}</div>
);

// ── KPI card ───────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, color, trend, badge }) => (
  <div className="analytics-kpi-card">
    <div className="analytics-kpi-card__top">
      <div className="analytics-kpi-card__icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      {trend != null ? (
        <span className={`analytics-kpi-card__trend analytics-kpi-card__trend--${trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat'}`}>
          {trend > 0 ? <FaArrowUp /> : trend < 0 ? <FaArrowDown /> : <FaMinus />}
          {Math.abs(trend)}%
        </span>
      ) : badge != null ? (
        <span className="analytics-kpi-card__badge" style={{ background: `${color}18`, color }}>{badge}</span>
      ) : null}
    </div>
    <p className="analytics-kpi-card__value">{value}</p>
    <p className="analytics-kpi-card__label">{label}</p>
  </div>
);

// ── Collection rate bar ────────────────────────────────────────────────────────
const CollRate = ({ rate }) => {
  const barColor = rate >= 75 ? P.green : rate >= 40 ? P.amber : P.orange;
  const note = rate >= 75
    ? 'Excellent — most invoices are paid'
    : rate >= 40
    ? 'Moderate — follow up on pending payments'
    : 'Low — many payments outstanding';
  return (
    <div className="analytics__collrate">
      <div className="analytics__collrate-header">
        <span className="analytics__collrate-lbl">Collection Rate</span>
        <span className="analytics__collrate-pct" style={{ color: barColor }}>{rate}%</span>
      </div>
      <div className="analytics__collrate-track">
        <div className="analytics__collrate-fill" style={{ width: `${Math.min(rate, 100)}%`, background: barColor }} />
      </div>
      <p className="analytics__collrate-note">{note}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Analytics = ({ orderType = 'own' }) => {
  const [range, setRange] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);

  // ── Draggable chart order (charts only — KPIs stay fixed) ────────────────
  // Instant paint from localStorage, then backend per-user order wins.
  const [chartOrder, setChartOrder] = useState(() => {
    try {
      return sanitizeOrder(JSON.parse(localStorage.getItem(ANALYTICS_ORDER_KEY)));
    } catch {
      return [...DEFAULT_CHART_ORDER];
    }
  });
  const [layoutSaving, setLayoutSaving] = useState(false);
  const saveTimer = useRef(null);

  const persistOrder = useCallback((order) => {
    const clean = sanitizeOrder(order);
    try {
      localStorage.setItem(ANALYTICS_ORDER_KEY, JSON.stringify(clean));
    } catch { /* storage full / private mode — backend still saves */ }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setLayoutSaving(true);
      try {
        await apiHelper.put('/settings/analytics-layout', { order: clean });
        apiHelper.invalidateGet('/settings');
      } catch {
        // Offline or unauthorized — localStorage already holds the order
      } finally {
        setLayoutSaving(false);
      }
    }, 600);
  }, []);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  // Load per-user order from backend (syncs across devices)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await apiHelper.get('/settings');
        const remote = settings?.analyticsLayout?.order;
        if (!cancelled && Array.isArray(remote) && remote.length > 0) {
          const clean = sanitizeOrder(remote);
          setChartOrder(clean);
          try {
            localStorage.setItem(ANALYTICS_ORDER_KEY, JSON.stringify(clean));
          } catch { /* ignore */ }
        }
      } catch { /* fall back to localStorage order */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const isMobileLayout = useIsMobileLayout();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleChartDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setChartOrder((prev) => {
      const from = prev.indexOf(active.id);
      const to = prev.indexOf(over.id);
      if (from < 0 || to < 0) return prev;
      const next = arrayMove(prev, from, to);
      persistOrder(next);
      return next;
    });
  }, [persistOrder]);

  const handleResetLayout = useCallback(() => {
    setChartOrder([...DEFAULT_CHART_ORDER]);
    persistOrder([...DEFAULT_CHART_ORDER]);
  }, [persistOrder]);

  useEffect(() => {
    if (range !== 'custom' || customFrom || customTo) return;
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    setCustomFrom(first.toISOString().slice(0, 10));
    setCustomTo(today.toISOString().slice(0, 10));
  }, [range]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/analytics?range=${range}&orderType=${orderType}`;
      if (range === 'custom') {
        if (customFrom) url += `&from=${customFrom}`;
        if (customTo)   url += `&to=${customTo}`;
      }
      setData(await apiHelper.get(url));
    } catch {
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo, orderType]);

  useEffect(() => {
    if (range === 'custom' && (!customFrom || !customTo)) return;
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const onOut = (e) => { if (rangeRef.current && !rangeRef.current.contains(e.target)) setRangeOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setRangeOpen(false); };
    document.addEventListener('mousedown', onOut);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onOut); document.removeEventListener('keydown', onEsc); };
  }, []);

  // ── Merged timeline for combo / quotation charts ───────────────────────────
  const combinedTimeline = useMemo(() => {
    if (!data) return [];
    const revMap = Object.fromEntries(data.revenueTimeline.map(d => [d.period, d]));
    const qMap   = Object.fromEntries((data.quotationsTimeline || []).map(d => [d.period, d.count]));
    return data.eventsTimeline.map(ev => ({
      ...ev,
      total:      revMap[ev.period]?.total   ?? 0,
      paid:       revMap[ev.period]?.paid    ?? 0,
      pending:    revMap[ev.period]?.pending ?? 0,
      quotations: qMap[ev.period]            ?? 0,
    }));
  }, [data]);

  // ── Trend: compare 2nd half vs 1st half of the selected period ─────────────
  const revTrend = useMemo(() => {
    const tl = data?.revenueTimeline;
    if (!tl?.length || tl.length < 2) return null;
    const mid = Math.floor(tl.length / 2);
    const a = tl.slice(0, mid).reduce((s, d) => s + d.total, 0);
    const b = tl.slice(mid).reduce((s, d) => s + d.total, 0);
    if (a === 0) return b > 0 ? 100 : 0;
    return Math.round(((b - a) / a) * 100);
  }, [data]);

  const evtTrend = useMemo(() => {
    const tl = data?.eventsTimeline;
    if (!tl?.length || tl.length < 2) return null;
    const mid = Math.floor(tl.length / 2);
    const a = tl.slice(0, mid).reduce((s, d) => s + d.count, 0);
    const b = tl.slice(mid).reduce((s, d) => s + d.count, 0);
    if (a === 0) return b > 0 ? 100 : 0;
    return Math.round(((b - a) / a) * 100);
  }, [data]);

  // ── Profit trend: 2nd half vs 1st half of profit timeline ──────────────────
  const profitTrend = useMemo(() => {
    const tl = data?.profitTimeline;
    if (!tl?.length || tl.length < 2) return null;
    const mid = Math.floor(tl.length / 2);
    const a = tl.slice(0, mid).reduce((s, d) => s + (d.profit || 0), 0);
    const b = tl.slice(mid).reduce((s, d) => s + (d.profit || 0), 0);
    if (a === 0) return b > 0 ? 100 : b < 0 ? -100 : 0;
    return Math.round(((b - a) / Math.abs(a)) * 100);
  }, [data]);

  // ── Derived display values ─────────────────────────────────────────────────
  const collRate    = data?.summary?.collectionRate ?? 0;
  const profitTotal  = data?.summary?.profitTotal ?? 0;
  const profitMargin = data?.summary?.profitMargin ?? 0;
  const teamCostTotal = data?.summary?.teamCostTotal ?? data?.teamStats?.total ?? 0;
  const overdueTotal  = data?.summary?.overdueTotal ?? data?.cashflow?.overdueTotal ?? 0;
  const repeatRate    = data?.summary?.repeatRate ?? data?.repeatClientStats?.repeatRate ?? 0;
  const momGrowth     = data?.summary?.momGrowth;

  const profitTimeline = data?.profitTimeline || [];
  const teamCostTimeline = data?.teamCostTimeline || [];
  const serviceRevenue = data?.serviceRevenue || [];
  const aging = data?.aging || [];
  const overdueInvoices = data?.overdueInvoices || [];
  const teamStats = data?.teamStats || { total: 0, paid: 0, pending: 0, leaderboard: [] };
  const cashflow = data?.cashflow || { overdueTotal: 0, dueSoonTotal: 0, teamPendingTotal: 0, upcomingEvents30: 0 };
  const topClients = data?.topClients || [];
  const repeatStats = data?.repeatClientStats || { totalClients: 0, repeatClients: 0, repeatRate: 0 };
  const projectsBreakdown = data?.projectsBreakdown || [];
  const productivity = data?.productivity || {};

  const hasProfit   = profitTimeline.some(d => (d.revenue || 0) > 0 || (d.teamCost || 0) > 0);
  const hasTeam     = (teamStats.total || 0) > 0 || teamCostTimeline.some(d => (d.cost || 0) > 0);
  const hasSvcRev   = serviceRevenue.length > 0;
  const hasAging    = aging.some(b => (b.count || 0) > 0);
  const hasOverdue  = overdueInvoices.length > 0;
  const hasClients  = topClients.length > 0;
  const hasProjects = projectsBreakdown.length > 0;
  const hasProductivity = (productivity.totalNotes || 0) > 0 || (productivity.totalChecklists || 0) > 0 || (productivity.checklistTotal || 0) > 0 || (productivity.totalProjects || 0) > 0;

  const revPieData  = data ? [
    { name: 'Collected',   value: data.revenueBreakdown.paid    },
    { name: 'Outstanding', value: data.revenueBreakdown.pending },
  ].filter(d => d.value > 0) : [];

  const statusPieData  = (data?.eventStatusBreakdown  || []).filter(d => d.count > 0);
  const billStatusData = (data?.billingStatusBreakdown || []).filter(d => d.count > 0);

  const hasRevTl      = combinedTimeline.some(d => d.total > 0);
  const hasCombo      = combinedTimeline.some(d => d.count > 0 || d.total > 0);
  const hasServices   = (data?.topServices?.length ?? 0) > 0;
  const hasEvtStatus  = statusPieData.length > 0;
  const hasBillStatus = billStatusData.length > 0;
  const hasQvsE       = combinedTimeline.some(d => d.quotations > 0 || d.count > 0);

  const rangeLabel = RANGE_OPTIONS.find(o => o.value === range)?.label || 'Month';

  // ── Draggable chart registry (charts only — KPI grid above stays fixed) ──
  // Each entry keeps its original title / subtitle / content verbatim; the
  // sortable grid below renders them in the user's saved order.
  const FULL_SPAN_CHARTS = useMemo(() => new Set([
    'revenue-trend', 'events-revenue', 'profit-trend',
  ]), []);

  const chartRegistry = {
    'revenue-trend': {
      title: 'Revenue Trend',
      subtitle: 'Total billed · collected · outstanding over time — see if earnings are growing',
      visible: true,
      node: hasRevTl ? (
        <>
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={data.revenueTimeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <defs>
                {[['gT', P.indigo], ['gP', P.green], ['gO', P.orange]].map(([id, c]) => (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={c} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={c} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={62} />
              <Tooltip content={<ChartTooltip formatters={{ total: 'inr', paid: 'inr', pending: 'inr' }} />} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
              <Area type="monotone" dataKey="total"   name="Total"       stroke={P.indigo} strokeWidth={2.5} fill="url(#gT)" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="paid"    name="Collected"   stroke={P.green}  strokeWidth={2}   fill="url(#gP)" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="pending" name="Outstanding" stroke={P.orange} strokeWidth={2}   fill="url(#gO)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
          <CollRate rate={collRate} />
        </>
      ) : <Empty />,
    },
    'events-revenue': {
      title: 'Events & Revenue Overview',
      subtitle: 'Bars = billed revenue (₹) · Line = event count — shows how busy periods drive income',
      visible: true,
      node: hasCombo ? (
        <ResponsiveContainer width="100%" height={230}>
          <ComposedChart data={combinedTimeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis yAxisId="rev" tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={62} />
            <YAxis yAxisId="cnt" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={28} />
            <Tooltip content={<ChartTooltip formatters={{ total: 'inr', count: 'num' }} />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
            <Bar  yAxisId="rev" dataKey="total" name="Revenue" fill={P.indigo} radius={[4, 4, 0, 0]} opacity={0.85} barSize={14} />
            <Line yAxisId="cnt" type="monotone" dataKey="count" name="Events" stroke={P.violet} strokeWidth={2.5} dot={{ r: 3, fill: P.violet }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : <Empty />,
    },
    'payment-health': {
      title: 'Payment Health',
      subtitle: 'Stacked collected vs outstanding — spot slow-paying periods',
      visible: true,
      node: hasRevTl ? (
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={data.revenueTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barSize={12}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={58} />
            <Tooltip content={<ChartTooltip formatters={{ paid: 'inr', pending: 'inr' }} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="paid"    name="Collected"   stackId="ph" fill={P.green}  radius={[0, 0, 0, 0]} />
            <Bar dataKey="pending" name="Outstanding" stackId="ph" fill={P.orange} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : <Empty />,
    },
    'revenue-split': {
      title: 'Revenue Split',
      subtitle: 'Collected vs outstanding — collection efficiency at a glance',
      visible: true,
      node: revPieData.length > 0 ? (
        <div className="analytics__pie-wrap">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={revPieData}
                cx="50%" cy="50%"
                innerRadius={56} outerRadius={84}
                paddingAngle={3}
                dataKey="value"
                labelLine={false}
                label={<PiePctLabel />}
              >
                <Cell fill={P.green} />
                <Cell fill={P.orange} />
              </Pie>
              <Tooltip formatter={(v) => inrFmt.format(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : <Empty msg="No billing data." />,
    },
    'booked-services': {
      title: 'Most Booked Services',
      subtitle: 'Which service types drive your business',
      visible: hasServices,
      node: (
        <ResponsiveContainer width="100%" height={Math.max((data?.topServices?.length ?? 0) * 38 + 24, 160)}>
          <BarChart
            data={data?.topServices || []}
            layout="vertical"
            margin={{ top: 4, right: 20, left: 0, bottom: 0 }}
            barSize={13}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="count" name="Bookings" radius={[0, 4, 4, 0]}>
              {(data?.topServices || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ),
    },
    'event-pipeline': {
      title: 'Event Pipeline',
      subtitle: 'Status of all events — see where your pipeline stands',
      visible: hasEvtStatus,
      node: (
        <div className="analytics__pie-wrap">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={statusPieData}
                dataKey="count" nameKey="status"
                cx="50%" cy="50%"
                innerRadius={52} outerRadius={82}
                paddingAngle={3}
                labelLine={false}
                label={<PiePctLabel />}
              >
                {statusPieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ),
    },
    'invoice-status': {
      title: 'Invoice Status',
      subtitle: 'Count of invoices by payment status',
      visible: hasBillStatus,
      node: (
        <div className="analytics__pie-wrap">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie
                data={billStatusData}
                dataKey="count" nameKey="status"
                cx="50%" cy="50%"
                innerRadius={52} outerRadius={82}
                paddingAngle={3}
                labelLine={false}
                label={<PiePctLabel />}
              >
                {billStatusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ),
    },
    'quotations-events': {
      title: 'Quotations vs Events',
      subtitle: 'How many leads converted to confirmed events per period',
      visible: hasQvsE,
      node: (
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={combinedTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={3} barSize={10}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={28} />
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="quotations" name="Quotations" fill={P.pink}   radius={[4, 4, 0, 0]} />
            <Bar dataKey="count"      name="Events"     fill={P.violet} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ),
    },
    'profit-trend': {
      title: 'Profit Trend',
      subtitle: `Revenue minus team payouts & extra charges · margin ${profitMargin}% · total profit ${compactInr(profitTotal)}`,
      visible: hasProfit,
      node: (
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={profitTimeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <defs>
              {[['gRev', P.indigo], ['gCost', P.orange], ['gProfit', P.teal]].map(([id, c]) => (
                <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={c} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={c} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={62} />
            <Tooltip content={<ChartTooltip formatters={{ revenue: 'inr', teamCost: 'inr', extraCharges: 'inr', profit: 'inr' }} />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
            <Area type="monotone" dataKey="revenue" name="Revenue" stroke={P.indigo} strokeWidth={2.5} fill="url(#gRev)" dot={false} activeDot={{ r: 4 }} />
            <Area type="monotone" dataKey="teamCost" name="Team cost" stroke={P.orange} strokeWidth={2} fill="url(#gCost)" dot={false} activeDot={{ r: 4 }} />
            <Area type="monotone" dataKey="profit" name="Profit" stroke={P.teal} strokeWidth={2.5} fill="url(#gProfit)" dot={false} activeDot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      ),
    },
    'team-cost': {
      title: 'Team Cost & Payouts',
      subtitle: `Total ${compactInr(teamStats.total)} · paid ${compactInr(teamStats.paid)} · pending ${compactInr(teamStats.pending)}`,
      visible: hasTeam,
      node: (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={teamCostTimeline} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
              <YAxis tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={58} />
              <Tooltip content={<ChartTooltip formatters={{ cost: 'inr' }} />} />
              <Bar dataKey="cost" name="Team payouts" fill={P.violet} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {(teamStats.leaderboard?.length ?? 0) > 0 && (
            <div className="analytics__leader-list">
              {teamStats.leaderboard.slice(0, 5).map((m) => (
                <div key={m.name} className="analytics__leader-row">
                  <span className="analytics__leader-name"><FaUsers /> {m.name}</span>
                  <span className="analytics__leader-val">{compactInr(m.total)}</span>
                  {m.pending > 0 && <span className="analytics__leader-due">{compactInr(m.pending)} due</span>}
                </div>
              ))}
            </div>
          )}
        </>
      ),
    },
    'revenue-per-service': {
      title: 'Revenue per Service',
      subtitle: 'Which services actually earn (₹ from invoices), not just bookings',
      visible: hasSvcRev,
      node: (
        <ResponsiveContainer width="100%" height={Math.max(serviceRevenue.length * 38 + 24, 180)}>
          <BarChart
            data={serviceRevenue}
            layout="vertical"
            margin={{ top: 4, right: 20, left: 0, bottom: 0 }}
            barSize={13}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis type="number" tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} />
            <Tooltip content={<ChartTooltip formatters={{ revenue: 'inr', bookings: 'num' }} />} />
            <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
              {serviceRevenue.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ),
    },
    'receivables-aging': {
      title: 'Receivables Aging',
      subtitle: `Overdue (>30d) ${compactInr(cashflow.overdueTotal)} · due soon ${compactInr(cashflow.dueSoonTotal)} · team pending ${compactInr(cashflow.teamPendingTotal)}`,
      visible: hasAging,
      node: (
        <>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={aging} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barSize={22}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} interval={0} />
              <YAxis tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} width={58} />
              <Tooltip content={<ChartTooltip formatters={{ amount: 'inr', count: 'num' }} />} />
              <Bar dataKey="amount" name="Outstanding" radius={[4, 4, 0, 0]}>
                {aging.map((_, i) => <Cell key={i} fill={i >= 2 ? P.orange : i === 1 ? P.amber : P.green} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="analytics__mini-grid">
            <div className="analytics__mini"><span>Upcoming events (30d)</span><strong>{cashflow.upcomingEvents30}</strong></div>
            <div className="analytics__mini"><span>Overdue</span><strong>{compactInr(cashflow.overdueTotal)}</strong></div>
            <div className="analytics__mini"><span>Team to pay</span><strong>{compactInr(cashflow.teamPendingTotal)}</strong></div>
          </div>
        </>
      ),
    },
    'oldest-unpaid': {
      title: 'Oldest Unpaid Invoices',
      subtitle: 'Follow up on these first — sorted by days outstanding',
      visible: hasOverdue,
      node: (
        <div className="analytics__leader-list">
          {overdueInvoices.slice(0, 6).map((inv) => (
            <div key={`${inv.id}-${inv.date}`} className="analytics__leader-row">
              <span className="analytics__leader-name"><FaExclamationTriangle /> {inv.clientName}</span>
              <span className="analytics__leader-val">{compactInr(inv.pending)}</span>
              <span className="analytics__leader-due">{inv.ageDays}d old</span>
            </div>
          ))}
        </div>
      ),
    },
    'top-clients': {
      title: 'Top Clients & Repeat Business',
      subtitle: `${repeatStats.repeatClients} of ${repeatStats.totalClients} clients booked 2+ events (${repeatStats.repeatRate}% repeat)`,
      visible: hasClients,
      node: (
        <>
          <ResponsiveContainer width="100%" height={Math.max(topClients.length * 38 + 24, 180)}>
            <BarChart
              data={topClients}
              layout="vertical"
              margin={{ top: 4, right: 20, left: 0, bottom: 0 }}
              barSize={13}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
              <XAxis type="number" tickFormatter={compactInr} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" width={118} tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} />
              <Tooltip content={<ChartTooltip formatters={{ revenue: 'inr', events: 'num', pending: 'inr' }} />} />
              <Bar dataKey="revenue" name="Revenue" fill={P.indigo} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="analytics__mini-grid">
            <div className="analytics__mini"><span><FaRedo /> Repeat rate</span><strong>{repeatStats.repeatRate}%</strong></div>
            <div className="analytics__mini"><span>Total clients</span><strong>{repeatStats.totalClients}</strong></div>
            {momGrowth != null && <div className="analytics__mini"><span>MoM revenue</span><strong>{momGrowth >= 0 ? '+' : ''}{momGrowth}%</strong></div>}
          </div>
        </>
      ),
    },
    'projects-productivity': {
      title: 'Projects & Productivity',
      subtitle: 'Project pipeline status plus notes & checklist completion',
      visible: hasProjects || hasProductivity,
      node: (
        <>
          {hasProjects && (
            <div className="analytics__pie-wrap">
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={projectsBreakdown}
                    dataKey="count" nameKey="status"
                    cx="50%" cy="50%"
                    innerRadius={52} outerRadius={82}
                    paddingAngle={3}
                    labelLine={false}
                    label={<PiePctLabel />}
                  >
                    {projectsBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {hasProductivity && (
            <div className="analytics__mini-grid">
              <div className="analytics__mini"><span><FaFolderOpen /> Projects</span><strong>{productivity.totalProjects ?? 0}</strong></div>
              <div className="analytics__mini"><span><FaFileAlt /> Notes ({productivity.pinnedNotes ?? 0} pinned)</span><strong>{productivity.totalNotes ?? 0}</strong></div>
              <div className="analytics__mini"><span><FaTasks /> Checklists</span><strong>{productivity.totalChecklists ?? 0} cards {productivity.checklistDone ?? 0}/{productivity.checklistTotal ?? 0} items ({productivity.checklistRate ?? 0}%)</strong></div>
            </div>
          )}
          {!hasProjects && !hasProductivity && <Empty />}
        </>
      ),
    },
  };

  // User order first, then any new / missing ids appended; hidden cards dropped.
  const visibleCharts = useMemo(() => {
    const ordered = sanitizeOrder(chartOrder)
      .filter((id) => chartRegistry[id]?.visible)
      .map((id) => ({ id, ...chartRegistry[id], spanFull: FULL_SPAN_CHARTS.has(id) }));
    return ordered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartOrder, data, FULL_SPAN_CHARTS]);

  return (
    <div className="analytics">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="analytics__header">
        <div className="analytics__title-row">
          <FaChartLine className="analytics__title-icon" />
          <h2 className="analytics__title">Business Analytics</h2>
          <span className="analytics__order-badge">
            {orderType === 'suborder' ? 'Sub Order' : 'Own Order'}
          </span>
        </div>

        <div className="analytics__controls">
          <span className="analytics__drag-hint" title="Drag any chart card by its grip handle to rearrange. Order saves to your account and syncs across devices.">
            <FaGripVertical /> Drag charts to rearrange{layoutSaving ? ' · saving…' : ''}
          </span>
          <button type="button" className="analytics__reset-btn" onClick={handleResetLayout} title="Reset chart order to default">
            <FaUndo /> Reset
          </button>
          <div className="analytics__range-select" ref={rangeRef}>
            <button
              type="button" className="analytics__range-btn"
              onClick={() => setRangeOpen(v => !v)}
              aria-haspopup="listbox" aria-expanded={rangeOpen}
            >
              <span>{rangeLabel}</span>
              <FaChevronDown />
            </button>
            {rangeOpen && (
              <div className="analytics__range-menu" role="listbox">
                {RANGE_OPTIONS.map(o => (
                  <button
                    key={o.value} type="button"
                    className={`analytics__range-option${range === o.value ? ' analytics__range-option--active' : ''}`}
                    onClick={() => { setRange(o.value); setRangeOpen(false); }}
                    role="option" aria-selected={range === o.value}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {range === 'custom' && (
            <div className="analytics__custom-range">
              <label className="analytics__date-field">
                <span>From</span>
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
              </label>
              <label className="analytics__date-field">
                <span>To</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
              </label>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="analytics__loading">
          <div className="analytics__spinner" />
          <span>Loading analytics…</span>
        </div>
      )}

      {!loading && error && (
        <div className="analytics__error">
          <p>{error}</p>
          <button type="button" onClick={fetchData}>Retry</button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* ── 1. KPI Cards ────────────────────────────────────────────────── */}
          <div className="analytics__kpi-grid">
            <KpiCard icon={<FaCalendarCheck />} label="Events (period)"   value={data.summary.totalEvents}            color={P.violet} trend={evtTrend} />
            <KpiCard icon={<FaRupeeSign />}     label="Total Revenue"     value={compactInr(data.summary.totalRevenue)} color={P.indigo} trend={revTrend} />
            <KpiCard icon={<FaCheckCircle />}   label="Collected"         value={compactInr(data.summary.paidRevenue)}  color={P.green}  badge={`${collRate}%`} />
            <KpiCard icon={<FaClock />}         label="Outstanding"       value={compactInr(data.summary.pendingRevenue)} color={P.orange} />
            <KpiCard icon={<FaCoins />}         label="Profit (period)"   value={compactInr(profitTotal)}               color={P.teal}   trend={profitTrend} badge={`${profitMargin}% margin`} />
            <KpiCard icon={<FaUsers />}         label="Team Cost"         value={compactInr(teamCostTotal)}             color={P.violet} badge={teamStats.pending > 0 ? `${compactInr(teamStats.pending)} due` : null} />
            <KpiCard icon={<FaFileAlt />}       label="Quotations"        value={data.summary.totalQuotations}          color={P.pink} />
            <KpiCard icon={<FaExclamationTriangle />} label="Overdue (>30d)" value={compactInr(overdueTotal)}          color={P.orange} badge={momGrowth != null ? `MoM ${momGrowth >= 0 ? '+' : ''}${momGrowth}%` : `${repeatRate}% repeat`} />
          </div>

          {/* ── 2. Draggable charts — user order, saved per account ──────────── */}
          {/* Mobile (single column): vertical-only dragging + vertical list
              strategy so cards only move top/bottom and the page never
              shifts left/right. Desktop keeps free 2-D grid sorting. */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleChartDragEnd}
            modifiers={isMobileLayout ? [restrictToVerticalAxis] : []}
          >
            <SortableContext
              items={visibleCharts.map((c) => c.id)}
              strategy={isMobileLayout ? verticalListSortingStrategy : rectSortingStrategy}
            >
              <div className="analytics__charts-sortable">
                {visibleCharts.map(({ id, title, subtitle, node, spanFull }) => (
                  <SortableChartItem key={id} id={id} spanFull={spanFull} verticalOnly={isMobileLayout}>
                    {({ attributes, listeners }) => (
                      <ChartCard title={title} subtitle={subtitle} dragHandle={{ attributes, listeners }}>
                        {node}
                      </ChartCard>
                    )}
                  </SortableChartItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>

        </>
      )}
    </div>
  );
};

export default Analytics;
